import React, { forwardRef } from 'react';
import {
  PALETTE, FONT_SERIF, FONT_SANS, FONT_MONO,
  HaleMark, ToxIntelMark, AgMark, Backplate, Card, Eyebrow, Headline, Body, Bullet, Rule, BrandLockup,
} from './primitives';

/* Asset 5 — Executive One-Pager
 *
 * Single-page handout for investors, retailers, and pilot partners.
 * Headline: "Hale creates trusted product governance through independent
 * scientific review." Three pillars + chain-of-accountability strip +
 * pilot stats + ask. Letter-portrait orientation (8.5×11 → 850×1100). */

const VB_W = 850;
const VB_H = 1100;

const PILLARS = [
  { code: '01', title: 'Independent scientific review',
    body: 'Every Hale pathway is signed by an independent toxicologist running on ToxIntel review infrastructure.' },
  { code: '02', title: 'Portable documentation',
    body: 'Hale Reviewed™ and Verified™ records are reusable across Sephora, Credo, Whole Foods, Target, and Ulta.' },
  { code: '03', title: 'Reviewer-led escalation',
    body: 'Structured intake, audit trail, and reviewer queue — defensible governance, not consumer fear scoring.' },
];

const ExecutiveOnePagerAsset = forwardRef(function ExecutiveOnePagerAsset(_props, ref) {
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Hale executive one-pager"
    >
      <Backplate w={VB_W} h={VB_H} />

      {/* Letterhead */}
      <BrandLockup x={48} y={56} scale={0.85} />
      <text x={VB_W - 48} y={70} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2}>
        EXECUTIVE BRIEF · PILOT 2026
      </text>
      <Rule x1={48} x2={VB_W - 48} y={130} />

      {/* Headline */}
      <Eyebrow x={48} y={180} text="Independent scientific review · for modern brands" />
      <Headline x={48} y={236} text="Hale creates trusted" size={40} />
      <Headline x={48} y={282} text="product governance through" size={40} />
      <Headline x={48} y={328} text="independent scientific review." size={40} italic color={PALETTE.CORAL} />

      <Body
        x={48}
        y={376}
        size={14}
        color={PALETTE.MUTED}
        lineHeight={1.55}
        text={[
          'Hale is a brand-activation layer powered by ToxIntel review infrastructure,',
          'with independent scientific review provided by The Agyekum Group. Two pathways',
          '(Reviewed™ + Verified™) deliver portable, retailer-ready governance documentation.',
        ]}
      />

      {/* Pillars */}
      {PILLARS.map((p, i) => (
        <g key={p.code} transform={`translate(${48 + i * 252},480)`}>
          <Card x={0} y={0} w={232} h={180}>
            <g transform="translate(20,20)">
              <Eyebrow x={0} y={0} text={p.code} />
              <text x={0} y={42} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={20}>
                {p.title}
              </text>
              <Body x={0} y={86} text={p.body} size={12} color={PALETTE.MUTED} lineHeight={1.45} />
            </g>
          </Card>
        </g>
      ))}

      {/* Chain of accountability strip */}
      <Eyebrow x={48} y={710} text="Chain of accountability" />
      <g transform="translate(48,734)">
        <Card x={0} y={0} w={VB_W - 96} h={88} fill="rgba(11,20,38,0.04)">
          <g transform="translate(24,24)">
            {[
              { k: 'BRAND',    v: 'Applicant' },
              { k: 'HALE',     v: 'Program · pathways · badges' },
              { k: 'TOXINTEL', v: 'Review infrastructure' },
              { k: 'AG',       v: 'Independent scientific review' },
            ].map((s, i) => (
              <g key={s.k} transform={`translate(${i * 180},0)`}>
                <text x={0} y={0} fill={PALETTE.CORAL} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2} dominantBaseline="hanging">
                  {s.k}
                </text>
                <text x={0} y={30} fill={PALETTE.SLATE} fontFamily={FONT_SANS} fontSize={13} fontWeight={600}>
                  {s.v}
                </text>
              </g>
            ))}
          </g>
        </Card>
      </g>

      {/* Pilot stats */}
      <Eyebrow x={48} y={870} text="Pilot launch · 2026" />
      {[
        { k: 'Pilot brands',     v: '3' },
        { k: 'Review pathways',  v: '2' },
        { k: 'Service categories',v: '5' },
        { k: 'Retailer ready',   v: '5+' },
      ].map((s, i) => (
        <g key={s.k} transform={`translate(${48 + i * 190},890)`}>
          <text x={0} y={48} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={800} fontSize={44}>
            {s.v}
          </text>
          <text x={0} y={74} fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2}>
            {s.k.toUpperCase()}
          </text>
        </g>
      ))}

      {/* Footer */}
      <Rule x1={48} x2={VB_W - 48} y={1020} />
      <text x={48} y={1050} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={12}>
        partnerships@tagc.us · thehale.co · toxintel.tagc.us
      </text>
      <text x={VB_W - 48} y={1050} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2}>
        HALE × TOXINTEL · 2026
      </text>
    </svg>
  );
});

export default ExecutiveOnePagerAsset;
